import { JobProcedure, Fluid, FluidMud } from '../models/job-procedure.model';
import { PumpSchedule } from 'libs/models/entities/pump-schedule';
import { PumpScheduleStageMaterialModel, PumpScheduleStageModel } from 'libs/models/entities/pump-schedule-stage';
import { FluidModel } from 'libs/models/fluids/fluid.model';
import { PumpScheduleEventModel } from 'libs/models/entities/pump-schedule-event';
import { MudParameterModel } from 'libs/models/mud-parameter';
import { JobProcedureActivityDTO } from '../models/job-procedure-server.model';
import { Job } from 'libs/models/entities/job';
import { newGuid } from '../../../../../shared/constant';
import { ProcedureTypeEnum } from '../enums/activity.enum';
import { FLUID_TYPE_SCHEDULE, MATERIAL_TYPE, UnitType } from '../../../../../../../../../libs/constants';
import { IPumpScheduleFluidType } from '../../../../../../../../../libs/models';
import { ShoeTrackData } from '../../../cp-pump/cp-pump.store';
import { calculateMaterialQuantity } from 'libs/shared/calculations';
import { PumpDisplacementOption } from '../enums/pump-displacement-option.enum';

const LIQUID_CONCENTRATION_UOMS = ['gal/bbl', 'l/m3', 'l/m³'];

export interface FreshMaterialOverlay {
  concentrationUom?: string;
  sapMaterialNumber?: string;
  sapMaterialName?: string;
}

export function mapJobProceduresToPumpSchedules(
  procedures: JobProcedure[],
  jobId: string,
  linerData?: { isVersaFlexLiner: boolean },
  job?: Job,
  fluidTypes?: IPumpScheduleFluidType[],
  shoeTrackDataByOrder?: Map<number, ShoeTrackData>
): PumpSchedule[] {
  if (!procedures || procedures.length === 0) {
    return [];
  }

  const groupedByOrder = groupProceduresByPumpScheduleOrder(procedures);
  const pumpSchedules: PumpSchedule[] = [];

  groupedByOrder.forEach((procs, order) => {
    const pumpSchedule = createPumpScheduleFromProcedures(procs, order, jobId, linerData, job, fluidTypes, shoeTrackDataByOrder);
    pumpSchedules.push(pumpSchedule);
  });

  return pumpSchedules;
}

function groupProceduresByPumpScheduleOrder(procedures: JobProcedure[]): Map<number, JobProcedure[]> {
  return procedures.reduce((acc, proc) => {
    const order = proc.pumpScheduleOrder ?? 0;
    if (!acc.has(order)) {
      acc.set(order, []);
    }
    acc.get(order)!.push(proc);
    return acc;
  }, new Map<number, JobProcedure[]>());
}

function createPumpScheduleFromProcedures(
  procedures: JobProcedure[],
  order: number,
  jobId: string,
  linerData?: { isVersaFlexLiner: boolean },
  job?: Job,
  fluidTypes?: IPumpScheduleFluidType[],
  shoeTrackDataByOrder?: Map<number, ShoeTrackData>
): PumpSchedule {
  const firstProc = procedures[0];
  const pumpSchedule = new PumpSchedule();

  // Use existing ID or generate new GUID for new tabs
  pumpSchedule.pumpScheduleId = firstProc.pumpScheduleId || newGuid();
  pumpSchedule.jobId = jobId;
  pumpSchedule.name = firstProc.pumpScheduleName || `Pump Schedule 0${order + 1}`;
  pumpSchedule.order = order;
  pumpSchedule.isVersaFlexLiner = linerData?.isVersaFlexLiner ?? null;

  pumpSchedule.controlPointType = 1;

  // Preserve all non-JobProcedure fields from existing pump schedules in the job
  const existingPumpSchedule = job?.pumpSchedules?.find(ps => ps.order === order);

  // iCem/HDF Import Flags
  pumpSchedule.isImportDataFromiCem = existingPumpSchedule?.isImportDataFromiCem ?? job?.isImportDataFromiCem ?? false;
  pumpSchedule.isImportDataFromHDF = existingPumpSchedule?.isImportDataFromHDF ?? false;
  pumpSchedule.iCemReplaced = existingPumpSchedule?.iCemReplaced ?? false;
  pumpSchedule.icemFileName = existingPumpSchedule?.icemFileName ?? null;
  // Backend expects non-nullable Guid: use existing value, or self-reference for new schedules
  pumpSchedule.originalPumpScheduleId = existingPumpSchedule?.originalPumpScheduleId ?? pumpSchedule.pumpScheduleId;
  pumpSchedule.isForceNew = false;

  // ShoeTrack / Liner / Schedule fields — prefer CpPumpStore values, fall back to existingPumpSchedule
  const storeShoeTrack = shoeTrackDataByOrder?.get(order);

  // Liner Fields
  pumpSchedule.linerCirculationMethod = storeShoeTrack?.linerCirculationMethod ?? existingPumpSchedule?.linerCirculationMethod ?? null;
  pumpSchedule.linerCementLength = storeShoeTrack?.linerCementLength ?? existingPumpSchedule?.linerCementLength ?? null;

  // Shoe Track Fields
  pumpSchedule.shoeTrackLength = storeShoeTrack?.shoeTrackLength ?? existingPumpSchedule?.shoeTrackLength ?? null;
  pumpSchedule.shoeTrackVolume = storeShoeTrack?.shoeTrackVolume ?? existingPumpSchedule?.shoeTrackVolume ?? null;

  // Schedule Configuration
  pumpSchedule.scheduledShutdown = storeShoeTrack?.scheduledShutdown ?? existingPumpSchedule?.scheduledShutdown ?? null;
  pumpSchedule.targetSafetyFactor = storeShoeTrack?.targetSafetyFactor ?? existingPumpSchedule?.targetSafetyFactor ?? null;
  pumpSchedule.batchMixingTime = storeShoeTrack?.batchMixingTime ?? existingPumpSchedule?.batchMixingTime ?? null;
  // Spacer Mix Method is a JOB-LEVEL field — always use PS1 (order 0) values for ALL pump schedules
  const ps1Store = shoeTrackDataByOrder?.get(0);
  const ps1Existing = job?.pumpSchedules?.find(ps => ps.order === 0);
  pumpSchedule.spacerMixMethod = ps1Store?.spacerMixMethod ?? ps1Existing?.spacerMixMethod ?? 0;
  pumpSchedule.isManuallySpacerMixMethod = ps1Store?.isManuallySpacerMixMethod ?? ps1Existing?.isManuallySpacerMixMethod ?? false;

  // Calculated/Aggregated Fields (server-side calculated, null for new data)
  pumpSchedule.dryWeight = existingPumpSchedule?.dryWeight ?? null;
  pumpSchedule.dryVolume = existingPumpSchedule?.dryVolume ?? null;
  pumpSchedule.totalCOGS = existingPumpSchedule?.totalCOGS ?? null;

  pumpSchedule.stages = buildStagesFromProcedures(procedures, pumpSchedule.pumpScheduleId, order, fluidTypes);
  pumpSchedule.jobProcedureActivities = buildJobProcedureActivities(procedures, order, pumpSchedule.pumpScheduleId);

  return pumpSchedule;
}


export function buildStagesFromProcedures(
  procedures: JobProcedure[],
  pumpScheduleId: string,
  order: number,
  fluidTypes?: IPumpScheduleFluidType[]
): PumpScheduleStageModel[] {
  const filteredProcedures = procedures
    .sort((a, b) => a.order - b.order);

  if (filteredProcedures.length === 0) return [];

  const stages: PumpScheduleStageModel[] = [];
  const stageGroups: { stageNumber: number | null; isPlug: boolean; procedures: JobProcedure[] }[] = [];

  // Group consecutive procedures with the same stage number
  // Null stages for plug activities (Bottom Plug, Top Plug, Plug/Dart) are individual stages
  // Shutdown activities (stage == null) join the preceding stage group as an event
  filteredProcedures.forEach(proc => {
    const lastGroup = stageGroups[stageGroups.length - 1];
    const isShutdown = proc.activityType.id === ProcedureTypeEnum.Shutdown;

    const isPlug = proc.activityType.id === ProcedureTypeEnum.BottomPlug
      || proc.activityType.id === ProcedureTypeEnum.TopPlugStartDisplacement
      || proc.activityType.id === ProcedureTypeEnum.PlugDart;

    if (isShutdown && lastGroup) {
      // Shutdown: add to the preceding stage group (becomes an event within that stage)
      lastGroup.procedures.push(proc);
    } else if (proc.stage == null && isPlug) {
      // Plug activities: always create individual stage
      stageGroups.push({ stageNumber: null, isPlug: true, procedures: [proc] });
    } else if (proc.stage == null && !isPlug) {
      // Non-plug activity that lost its stage (e.g., spacer with unlinked fluid):
      // No stage should be created for this activity (pumpScheduleStageId cleared in activityDTO)
      stageGroups.push({ stageNumber: null, isPlug: false, procedures: [proc] });
    } else if (lastGroup && lastGroup.stageNumber === proc.stage) {
      // Same stage number as previous: add to existing group
      lastGroup.procedures.push(proc);
    } else {
      // Look back for an earlier group with the same stage number (e.g., when an unlinked
      // spacer with stage=null sits between two same-stage cements, the null-stage group
      // interrupts the consecutive match but both cements should belong to the same stage).
      let priorGroup: typeof stageGroups[number] | null = null;
      if (proc.stage != null) {
        for (let i = stageGroups.length - 1; i >= 0; i--) {
          if (stageGroups[i].stageNumber === proc.stage && !stageGroups[i].isPlug) {
            priorGroup = stageGroups[i];
            break;
          }
        }
      }
      if (priorGroup) {
        priorGroup.procedures.push(proc);
      } else {
        // New stage number: create new group
        stageGroups.push({ stageNumber: proc.stage, isPlug: false, procedures: [proc] });
      }
    }
  });

  // Create stages with sequential order for ALL groups, including plug stages (null stageNumber).
  // Plug stages (Bottom Plug, Top Plug/Start Displacement, Plug/Dart) get number = -1
  // so they are persisted as PumpScheduleStage entities and shown on CP2/CP4.
  const usedStageIds = new Set<string>();
  // Use a separate counter (stageIndex) instead of the loop index because skipped groups
  // (null-stage, non-plug) advance the loop index without creating a stage, causing gaps.
  // stage.order must match the actual array position so that StageStateManager.number$
  // can correctly look up stageNumbers$[this.order].
  let stageIndex = 0;
  for (let index = 0; index < stageGroups.length; index++) {
    const group = stageGroups[index];

    // Non-plug activities that lost their stage (e.g., unlinked spacer) should NOT create a stage
    if (group.stageNumber == null && !group.isPlug) {
      continue;
    }

    const stage = createStageFromProcedures(
      group.procedures,
      group.stageNumber,
      stageIndex++,
      pumpScheduleId,
      order,
      fluidTypes
    );
    // Plug stages: set number = -1 (convention from state-factory)
    if (group.stageNumber == null && group.isPlug) {
      stage.number = -1;
    }
    if (stage.id && usedStageIds.has(stage.id)) {
      stage.id = null;
    }
    if (stage.id) {
      usedStageIds.add(stage.id);
    }
    stages.push(stage);
  }

  return stages;
}

export function createStageFromProcedures(
  procedures: JobProcedure[],
  stageNumber: number | null,
  stageOrder: number,
  pumpScheduleId: string,
  pumpScheduleOrder: number,
  fluidTypes?: IPumpScheduleFluidType[]
): PumpScheduleStageModel {
  const firstProc = procedures[0];
  const stage = new PumpScheduleStageModel();

  stage.id = firstProc.pumpScheduleStageId;
  stage.pumpScheduleId = pumpScheduleId;
  stage.order = stageOrder;
  stage.number = stageNumber;

  const fluidType: {id: string | null; name: string | null} = getFluidType(procedures, fluidTypes, stageNumber);

  stage.pumpScheduleFluidTypeId = fluidType.id;
  stage.pumpScheduleFluidTypeName = fluidType.name;


  if (stageNumber === 1 && procedures.length > 0) {
    const pumpMudProc = procedures.find(p => p.activityType.id === ProcedureTypeEnum.PumpMud);
    if (pumpMudProc && pumpMudProc.fluid) {
      stage.isMudType = true;
      stage.mudParameter = createMudParameter(pumpMudProc);
    }
  }

  if (firstProc.fluid && ((firstProc.fluid as any).isCement !== undefined || (firstProc.fluid as any).id)) {
    stage.slurry = mapFluidToSlurryModel(firstProc.fluid);
    stage.plannedDensity = firstProc.density || (firstProc.fluid as any).density;
  } else if (firstProc.density != null) {
    stage.plannedDensity = firstProc.density;
  }
  stage.topOfFluid = firstProc.topOfFluid?.value ?? null;

  const totalBulk = procedures.reduce((sum, p) => sum + (p.bulk?.value || 0), 0);
  stage.bulkCement = totalBulk > 0 ? totalBulk : null;

  const totalVolume = procedures.reduce((sum, p) => sum + (p.volume?.value || 0), 0);
  stage.plannedVolume = totalVolume > 0 ? totalVolume : null;

  stage.events = procedures.map(proc => createEventFromProcedure(proc));

  return stage;
}

function getFluidType(
  procedures: JobProcedure[],
  fluidTypes?: IPumpScheduleFluidType[],
  stageNumber?: number | null
): { id: string | null; name: string | null }{
  const procedure = procedures[0];
  if (!procedure || !fluidTypes) return {id: null, name: null};

  const findFluidType = (fluidTypeName: string): { id: string | null; name: string | null } => {
    const found = fluidTypes.find(fl => fl.name.toUpperCase() === fluidTypeName.toUpperCase());
    return found ? { id: found.id, name: found.name } : { id: null, name: null };
  };

  switch (procedure.activityType.id) {
    case ProcedureTypeEnum.PumpDisplacement: {
      // Map displacement options to Spacer or Mud fluid types
      const displacementOption = (procedure.activityType as any).displacementOption as number;
      if (displacementOption) {
        // Fresh Water, Sea Water, Spacer → Spacer
        if (displacementOption === PumpDisplacementOption.FreshWater ||
            displacementOption === PumpDisplacementOption.SeaWater ||
            displacementOption === PumpDisplacementOption.Spacer) {
          return findFluidType(FLUID_TYPE_SCHEDULE.SPACER_FLUSH);
        }
        // Brine, Water-Based Mud, OBM/SBM → Mud
        if (displacementOption === PumpDisplacementOption.Brine ||
            displacementOption === PumpDisplacementOption.Mud ||
            displacementOption === PumpDisplacementOption.OBM_SBM) {
          return findFluidType(FLUID_TYPE_SCHEDULE.MUD);
        }
      }
      // Fallback for PumpDisplacement without displacement option
      return findFluidType(FLUID_TYPE_SCHEDULE.TOP_PLUG_START_DISPLACEMENT);
    }
    case ProcedureTypeEnum.TopPlugStartDisplacement:
      return findFluidType(FLUID_TYPE_SCHEDULE.TOP_PLUG_START_DISPLACEMENT);
    case ProcedureTypeEnum.PumpMud:
      return findFluidType(stageNumber === 1 ? FLUID_TYPE_SCHEDULE.DRILLING_FLUID_MUD : FLUID_TYPE_SCHEDULE.MUD);
    case ProcedureTypeEnum.PumpCement:
      return findFluidType(FLUID_TYPE_SCHEDULE.CEMENT);
    case ProcedureTypeEnum.PumpSpacer:
      return findFluidType(FLUID_TYPE_SCHEDULE.SPACER_FLUSH);
    case ProcedureTypeEnum.PlugDart:
      return findFluidType(FLUID_TYPE_SCHEDULE.PLUG_DART);
    case ProcedureTypeEnum.BottomPlug:
      return findFluidType(FLUID_TYPE_SCHEDULE.BOTTOM_PLUG);
    default:
      return { id: null, name: null };
  }
}

function createMudParameter(procedure: JobProcedure): MudParameterModel {
  const mud = procedure.fluid as FluidMud;
  const mudParam = new MudParameterModel();

  if (mud) {
    mudParam.id = mud.id;
    mudParam.mudName = mud.fluidName;
    mudParam.mudTypeId = mud.mudType?.id;
    mudParam.typeName = mud.mudType?.typeName;
    mudParam.mudDensity = procedure.density;
  }

  return mudParam;
}

function mapFluidToSlurryModel(fluid: Fluid | FluidMud): any {
  if (!fluid) return null;

  return {
    id: (fluid as any).id,
    name: (fluid as any).name,
    displayName: (fluid as any).displayName,
    slurryType: (fluid as any).slurryType,
    density: (fluid as any).density,
    yield: (fluid as any).yield,
    sackWeight: (fluid as any).sackWeight,
    isCement: (fluid as any).isCement,
    isSpacer: (fluid as any).isSpacer,
    slurryId: (fluid as any).slurryId,
    slurryNo: (fluid as any).slurryNo,
    iFactsId: (fluid as any).iFactsId
  };
}

function createEventFromProcedure(procedure: JobProcedure): PumpScheduleEventModel {
  const event = new PumpScheduleEventModel();

  event.pumpScheduleStageId = procedure.pumpScheduleStageId;
  event.pumpScheduleEventId = procedure.id;
  event.placementMethod = procedure.placementMethod?.value;
  event.rate = procedure.rate;
  event.volume = procedure.volume?.value ?? null;
  event.topOfFluid = procedure.topOfFluid?.value ?? null;
  event.length = procedure.length?.value ?? null;
  event.bulkCement = procedure.bulk?.value ?? null;
  event.order = procedure.order;

  return event;
}

function buildJobProcedureActivities(
  procedures: JobProcedure[],
  order: number,
  pumpScheduleId: string
): JobProcedureActivityDTO[] {
  return procedures.map(proc => mapProcedureToActivityDTO(proc, pumpScheduleId));
}

function mapProcedureToActivityDTO(proc: JobProcedure, pumpScheduleId: string): JobProcedureActivityDTO {
  const activityDTO = {
    id: proc.id,
    jobProcedureActivityTypeId: proc.activityType.id,
    pumpScheduleId: pumpScheduleId,
    pumpScheduleStageId: proc.pumpScheduleStageId,
    order: proc.order,
    stageNumber: proc.stage,
    time: Number.isFinite(proc.time) ? proc.time : 0,
    rate: proc.rate,
    volume: proc.volume?.value ?? null,
    topOfFluid: proc.topOfFluid?.value ?? null,
    length: proc.length?.value ?? null,
    bulkCement: proc.bulk?.value ?? null,
    density: getDensity(proc),
    placementMethodId: proc.placementMethod?.value,
    slurry: mapFluidToSlurryDTO(proc.fluid),
    mudParameter: getMudParameter(proc),
    displacementOption: getDisplacementOption(proc)
  } as JobProcedureActivityDTO;
  // Clear stale pumpScheduleStageId for non-plug, non-shutdown activities that lost their stage
  const isPlugType = activityDTO.jobProcedureActivityTypeId === ProcedureTypeEnum.BottomPlug
    || activityDTO.jobProcedureActivityTypeId === ProcedureTypeEnum.TopPlugStartDisplacement
    || activityDTO.jobProcedureActivityTypeId === ProcedureTypeEnum.PlugDart;
  const isShutdownType = activityDTO.jobProcedureActivityTypeId === ProcedureTypeEnum.Shutdown;
  if (activityDTO.stageNumber == null && !isPlugType && !isShutdownType) {
    activityDTO.pumpScheduleStageId = null;
  }
  return activityDTO;
}

function getDensity(proc: JobProcedure): number | null {
  if (proc.density != null) return proc.density;
  if (proc.fluid && (proc.fluid as any).density != null) return (proc.fluid as any).density;
  return null;
}

function mapFluidToSlurryDTO(fluid: Fluid | FluidMud): any {
  if (!fluid) return null;

  const slurryData = fluid as any;
  if (!slurryData.slurryId && !slurryData.id) return null;

  return {
    id: slurryData.id,
    slurryId: slurryData.slurryId,
    slurryNo: slurryData.slurryNo,
    name: slurryData.name,
    displayName: slurryData.displayName,
    iFactsId: slurryData.iFactsId,
    isCement: slurryData.isCcement,
    isSpacer: slurryData.isSpacer,
    slurryType: slurryData.slurryType,
    density: slurryData.density,
    yield: slurryData.yield,
    sackWeight: slurryData.sackWeight
  };
}

function getMudParameter(proc: JobProcedure): any {
  if (!proc.fluid) return null;

  const mud = proc.fluid as any;
  if (!mud.mudType) return null;

  return {
    id: mud.id,
    mudTypeId: mud.mudType?.id,
    mudDensity: proc.density,
    mudName: mud.fluidName,
    typeName: mud.mudType?.typeName
  };
}

function getDisplacementOption(proc: JobProcedure): number | null {
  const activityType = proc.activityType as any;
  if (activityType.id === ProcedureTypeEnum.PumpDisplacement) {
    return activityType.displacementOption ?? null;
  }
  return null;
}

export function buildStageMaterialsFromSlurry(
  fluidMaterials: any[],
  parentFluid?: any,
  stageVolume?: number
): PumpScheduleStageMaterialModel[] {
  if (!fluidMaterials?.length) return [];
  return fluidMaterials.map(fm => {
    const sm = new PumpScheduleStageMaterialModel();
    sm.fluidMaterialId = fm.id;
    sm.fluidMaterialName = fm.materialName;
    sm.ifactMaterialId = fm.materialId;
    sm.cemBlendCompId = fm.cemBlendCompId;
    sm.concentration = fm.concentration;
    sm.concentrationUnit = fm.concentrationUnit || fm.fluidUnitMeasureName;
    sm.plannedVolumeUnit = fm.plannedVolumeUnit;
    sm.loadoutVolume = fm.loadoutVolume;
    sm.loadoutVolumeUnit = fm.loadoutVolumeUnit;
    sm.sapMaterialNumber = fm.sapMaterialNumber;
    sm.sapMaterialName = fm.sapMaterialName;
    sm.materialType = fm.materialType;
    sm.order = fm.order;
    sm.lotNo = fm.lotNo;
    sm.isChangeActualQty = false;
    sm.isWeightMaterial = fm.isWeightMaterial;
    sm.sg = fm.sg;
    sm.overrideMixingProcedureId = fm.mixingProcedureId || fm.overrideMixingProcedureId || null;
    if (fm.materialType === MATERIAL_TYPE.MIX_WATER) {
      sm.plannedQty = null;
      sm.actualQty = null;
      sm.loadoutVolume = null;
      sm.loadoutVolumeUnit = 0;
    } else if (fm.plannedQty != null) {
      sm.plannedQty = fm.plannedQty;
    } else if (parentFluid && stageVolume > 0) {
      const calcResult = calculateMaterialQuantity(
        fm.outputUnit,
        fm.sg,
        fm.testAmount,
        stageVolume,
        parentFluid.mixVolume,
        parentFluid.yield,
        fm.concentration,
        fm.concentrationUnit || fm.fluidUnitMeasureName,
        parentFluid.sackWeight,
        parentFluid.mixWater,
        parentFluid.waterDensity,
        parentFluid.density
      );
      const qty = calcResult?.Value ?? null;
      const unitType = calcResult?.UnitType ?? null;
      sm.plannedQty = qty;
      sm.plannedVolumeUnit = unitType;
      sm.loadoutVolume = qty;
      sm.loadoutVolumeUnit = unitType;
    }

    return sm;
  });
}

 //Overlays fresh concentrationUom/SAP data onto stage materials by fluidMaterialId.
 //Non-destructive: unmatched materials keep their original object reference.
function overlayFreshMaterialData(
  fluidMaterials: PumpScheduleStageMaterialModel[],
  freshMaterialsById?: Map<string, FreshMaterialOverlay>
): PumpScheduleStageMaterialModel[] {
  if (!freshMaterialsById?.size || !fluidMaterials?.length) {
    return fluidMaterials;
  }
  return fluidMaterials.map(sm => {
    const fresh = sm.fluidMaterialId && freshMaterialsById.get(sm.fluidMaterialId);
    if (!fresh) {
      return sm;
    }
    const normalizedUom = fresh.concentrationUom?.toLowerCase().replace(/\s+/g, '');
    const unitType = normalizedUom
      ? (LIQUID_CONCENTRATION_UOMS.includes(normalizedUom) ? UnitType.SmallVolume : UnitType.Weight)
      : null;
    return {
      ...sm,
      concentrationUnit: fresh.concentrationUom ?? sm.concentrationUnit,
      loadoutVolumeUnit: unitType ?? sm.loadoutVolumeUnit,
      plannedVolumeUnit: unitType ?? sm.plannedVolumeUnit,
      sapMaterialNumber: fresh.sapMaterialNumber ?? sm.sapMaterialNumber,
      sapMaterialName: fresh.sapMaterialName ?? sm.sapMaterialName
    };
  });
}

export function mergeStageDataFromUI(
  pumpSchedules: PumpSchedule[],
  pumpUiData: PumpSchedule[],
  actualPumpSchedules: PumpSchedule[],
  availableFluids: FluidModel[],
  freshMaterialsById?: Map<string, FreshMaterialOverlay>
): void {
  mergeStageThickeningTime(pumpSchedules, pumpUiData, actualPumpSchedules);
  mergeStageSpecificShutdownTime(pumpSchedules, pumpUiData, actualPumpSchedules);
  mergeStageDeadVolume(pumpSchedules, pumpUiData, actualPumpSchedules);
  mergeStageLoadoutVolume(pumpSchedules, pumpUiData, actualPumpSchedules);
  mergeStageMaterials(pumpSchedules, pumpUiData, actualPumpSchedules, availableFluids, freshMaterialsById);
}

function findMatchingPs(list: PumpSchedule[], ps: PumpSchedule): PumpSchedule | null {
  return list?.find(p => p.pumpScheduleId === ps.pumpScheduleId) // when a new pump schedule is added the id matching logic fails hence fall back to order similar to the way fall back to stage order when stage is added and id is not available in the backend
    ?? list?.find(p => p.order === ps.order)
    ?? null;
}

export function mergeStageMaterials(
  pumpSchedules: PumpSchedule[],
  pumpUiData: PumpSchedule[],
  actualPumpSchedules: PumpSchedule[],
  availableFluids: FluidModel[],
  freshMaterialsById?: Map<string, FreshMaterialOverlay>
): void {
  pumpSchedules.forEach(ps => {
    ps.stages?.forEach(stage => {
      if (!stage.id && !stage.slurry?.id) return;

      // 1. Try UI state (getAllDataPump) — has calculated quantities
      const uiPs = findMatchingPs(pumpUiData, ps);
      const uiStage = stage.id ? uiPs?.stages?.find(s => s.id === stage.id) : null;
      if (uiStage?.fluidMaterials?.length > 0) {
        stage.fluidMaterials = overlayFreshMaterialData(uiStage.fluidMaterials, freshMaterialsById);
        if (uiStage.slurry) { stage.slurry = uiStage.slurry; }
        return;
      }

      // 2. Try actualPumpSchedule stage fluidMaterials (from PumpScheduleStageMaterial in DB)
      const actualPs = findMatchingPs(actualPumpSchedules, ps);
      const actualStage = stage.id ? actualPs?.stages?.find(s => s.id === stage.id) : null;
      if (actualStage?.fluidMaterials?.length > 0) {
        stage.fluidMaterials = overlayFreshMaterialData(actualStage.fluidMaterials, freshMaterialsById);
        if (actualStage.slurry) { stage.slurry = actualStage.slurry; }
        return;
      }

      // 3. Try fluids$ — definitive source of slurry fluidMaterial from iFacts/DB
      const slurryId = stage.slurry?.id || actualStage?.slurry?.id;
      if (slurryId) {
        const fluid = availableFluids.find(f => f.id === slurryId);
        if (fluid?.fluidMaterial?.length > 0) {
          stage.fluidMaterials = overlayFreshMaterialData(
            buildStageMaterialsFromSlurry(fluid.fluidMaterial, fluid, stage.plannedVolume),
            freshMaterialsById
          );
          stage.slurry = fluid;
          return;
        }
      }

      // 4. Fallback: preserve actualStage slurry even without materials
      if (actualStage?.slurry) {
        stage.slurry = actualStage.slurry;
      }
    });
  });
}

export function mergeStageDeadVolume(
  pumpSchedules: PumpSchedule[],
  pumpUiData: PumpSchedule[],
  actualPumpSchedules: PumpSchedule[]
): void {
  pumpSchedules.forEach(ps => {
    const uiPs = findMatchingPs(pumpUiData, ps);
    const actualPs = findMatchingPs(actualPumpSchedules, ps);

    ps.stages?.forEach(stage => {
      const uiStage = stage.id
        ? uiPs?.stages?.find(s => s.id === stage.id)
        : uiPs?.stages?.find(s => !s.id && s.order === stage.order);
      const actualStage = stage.id
        ? actualPs?.stages?.find(s => s.id === stage.id)
        : null;

      // Use UI deadVolume only when it's a positive value or explicitly set by user.
      // A deadVolume of 0 from UI is the default form initialization (stage-state-manager
      // initializes with `this._model.deadVolume || 0`) and should not override the DB value.
      if (uiStage?.deadVolume != null && (uiStage.deadVolume > 0 || uiStage.isManuallyDeadVolume)) {
        stage.deadVolume = uiStage.deadVolume;
        stage.deadVolumeFluidType = uiStage.deadVolumeFluidType;
        stage.isManuallyDeadVolume = uiStage.isManuallyDeadVolume ?? stage.isManuallyDeadVolume;
        return;
      }

      if (actualStage?.deadVolume != null) {
        stage.deadVolume = actualStage.deadVolume;
        stage.deadVolumeFluidType = actualStage.deadVolumeFluidType;
        stage.isManuallyDeadVolume = actualStage.isManuallyDeadVolume ?? stage.isManuallyDeadVolume;
      }
    });
  });
}

export function mergeStageLoadoutVolume(
  pumpSchedules: PumpSchedule[],
  pumpUiData: PumpSchedule[],
  actualPumpSchedules: PumpSchedule[]
): void {
  pumpSchedules.forEach(ps => {
    const uiPs = findMatchingPs(pumpUiData, ps);
    const actualPs = findMatchingPs(actualPumpSchedules, ps);

    ps.stages?.forEach(stage => {
      const uiStage = stage.id
        ? uiPs?.stages?.find(s => s.id === stage.id)
        : uiPs?.stages?.find(s => !s.id && s.order === stage.order);
      const actualStage = stage.id
        ? (actualPs?.stages?.find(s => s.id === stage.id) ?? actualPs?.stages?.find(s => s.order === stage.order))
        : null;

      // UI state has the correct loadoutVolume (computed by StageCalculator._loadoutByDeadVolume$)
      if (uiStage?.loadoutVolume != null) {
        stage.loadoutVolume = uiStage.loadoutVolume;
        stage.isChangeLoadOutVolume = uiStage.isChangeLoadOutVolume ?? stage.isChangeLoadOutVolume;
      } else if (actualStage?.loadoutVolume != null) {
        stage.loadoutVolume = actualStage.loadoutVolume;
        stage.isChangeLoadOutVolume = actualStage.isChangeLoadOutVolume ?? stage.isChangeLoadOutVolume;
      }

      // After merging, if stage has deadVolume > 0 (set by mergeStageDeadVolume) but
      // loadoutVolume equals plannedVolume (dead volume not included), recalculate.
      // This handles cases where both UI and actual have stale loadoutVolume.
      if (stage.deadVolume > 0 && stage.plannedVolume > 0 && stage.loadoutVolume === stage.plannedVolume) {
        stage.loadoutVolume = stage.plannedVolume + stage.deadVolume;
      }
    });
  });
}

export function mergeStageSpecificShutdownTime(
  pumpSchedules: PumpSchedule[],
  pumpUiData: PumpSchedule[],
  actualPumpSchedules: PumpSchedule[]
): void {
  pumpSchedules.forEach(ps => {
    const uiPs = findMatchingPs(pumpUiData, ps);
    const actualPs = findMatchingPs(actualPumpSchedules, ps);

    ps.stages?.forEach(stage => {
      // when a new stage is added the id is not yet available hence fall back to order-based matching
      const uiStage = stage.id
        ? uiPs?.stages?.find(s => s.id === stage.id)
        : uiPs?.stages?.find(s => !s.id && s.order === stage.order);
      const actualStage = stage.id
        ? actualPs?.stages?.find(s => s.id === stage.id)
        : null;

      if (uiStage?.specificShutdownTime != null) {
        stage.specificShutdownTime = uiStage.specificShutdownTime;
        return;
      }

      if (actualStage?.specificShutdownTime != null) {
        stage.specificShutdownTime = actualStage.specificShutdownTime;
      }
    });
  });
}

export function mergeStageThickeningTime(
  pumpSchedules: PumpSchedule[],
  pumpUiData: PumpSchedule[],
  actualPumpSchedules: PumpSchedule[]
): void {
  pumpSchedules.forEach(ps => {
    const uiPs = findMatchingPs(pumpUiData, ps);
    const actualPs = findMatchingPs(actualPumpSchedules, ps);

    ps.stages?.forEach(stage => {

      // when a new stage is added the id is not yet available hence fall back to order-based matching
      const uiStage = stage.id
        ? uiPs?.stages?.find(s => s.id === stage.id)
        : uiPs?.stages?.find(s => !s.id && s.order === stage.order);
      const actualStage = stage.id
        ? actualPs?.stages?.find(s => s.id === stage.id)
        : null;

      // Prefer UI state (user may have manually entered or it was calculated from slurry)
      if (uiStage?.thickeningTime != null) {
        stage.thickeningTime = uiStage.thickeningTime;
        stage.isManualyThickeningTime = uiStage.isManualyThickeningTime ?? false;
        stage.is70BCOverrideThickeningTime = uiStage.is70BCOverrideThickeningTime ?? false;
        stage.thickeningTime70BC = uiStage.thickeningTime70BC ?? null;
        return;
      }

      // Fall back to actual pump schedule (persisted value from DB)
      if (actualStage?.thickeningTime != null) {
        stage.thickeningTime = actualStage.thickeningTime;
        stage.isManualyThickeningTime = actualStage.isManualyThickeningTime ?? false;
        stage.is70BCOverrideThickeningTime = actualStage.is70BCOverrideThickeningTime ?? false;
        stage.thickeningTime70BC = actualStage.thickeningTime70BC ?? null;
      }
    });
  });
}

